import React from 'react';
import {Link} from '~/components/Link';
import * as Icon from 'react-feather';
import {toast} from 'react-hot-toast';
import QtyForm from './QtyForm';
import type {
  Cart as CartType,
  CartCost,
  CartLine,
  CartLineUpdateInput,
} from '@shopify/hydrogen/storefront-api-types';
import * as _ from 'lodash';
import {flattenConnection, Image, Money} from '@shopify/hydrogen';
import {useFetcher} from '@remix-run/react';
import {CartAction} from '~/lib/type';

function CartLinePrice({
  line,
  priceType = 'regular',
  ...passthroughProps
}: {
  line: CartLine;
  priceType?: 'regular' | 'compareAt';
  [key: string]: any;
}) {
  if (!line?.cost?.amountPerQuantity || !line?.cost?.totalAmount) return null;

  const moneyV2 =
    priceType === 'regular'
      ? line.cost.totalAmount
      : line.cost.compareAtAmountPerQuantity;

  if (moneyV2 == null) {
    return null;
  }

  return <Money withoutTrailingZeros {...passthroughProps} data={moneyV2} />;
}

function ItemRemoveButton({lineIds}: {lineIds: CartLine['id'][]}) {
  const fetcher = useFetcher();

  return (
    <fetcher.Form action="/cart" method="post">
      <input
        type="hidden"
        name="cartAction"
        value={CartAction.REMOVE_FROM_CART}
      />
      <input type="hidden" name="linesIds" value={JSON.stringify(lineIds)} />
      <button
        className="flex items-center justify-center w-10 h-10 border rounded"
        type="submit"
      >
        <span className="sr-only">Remove</span>
        <Icon.Trash2 />
      </button>
    </fetcher.Form>
  );
}
const CartContent = ({
  lines: cartLines,
  cost,
  checkoutUrl,
}: {
  lines: CartType['lines'] | undefined;
  cost: CartCost | undefined;
  checkoutUrl: string;
}) => {
  const currentLines = cartLines ? flattenConnection(cartLines) : [];

  return (
    <>
      <div className="cart-table table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th scope="col">Product</th>
              <th scope="col">Name</th>
              <th scope="col">Unit Price</th>
              <th scope="col">Quantity</th>
              <th scope="col">Total</th>
            </tr>
          </thead>

          <tbody>
            {currentLines.length ? (
              currentLines.map((crt) => (
                <tr key={crt.id}>
                  <td className="product-thumbnail">
                    <Link to={`/products/${crt.id}`}>
                      <>
                        {crt.merchandise.image && (
                          <Image
                            width={110}
                            height={110}
                            data={crt.merchandise.image}
                            className="object-cover object-center w-24 h-24 border rounded md:w-28 md:h-28"
                            alt={crt.merchandise.title}
                          />
                        )}
                      </>
                    </Link>
                  </td>

                  <td className="product-name">
                    <Link to={`/products/${crt.id}`}>
                      {crt.merchandise?.product?.handle ? (
                        <Link
                          to={`/products/${crt.merchandise.product.handle}`}
                        >
                          {crt.merchandise?.product?.title || ''}
                        </Link>
                      ) : (
                        <span>{crt.merchandise?.product?.title || ''}</span>
                      )}
                    </Link>
                  </td>

                  <td className="product-price">
                    <span className="unit-amount">
                      <CartLinePrice line={crt} as="span" />
                    </span>
                  </td>

                  <td className="product-quantity">
                    <QtyForm line={crt} />
                  </td>

                  <td className="product-subtotal">
                    <span className="subtotal-amount">
                      {/* ${(crt.quantity * crt.price).toFixed(2)} */}
                    </span>

                    <span>
                      <ItemRemoveButton lineIds={[crt.id]} />
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="text-center">
                  Empty
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="cart-buttons">
        <div className="row align-items-center">
          <div className="col-lg-7 col-md-7 col-sm-7">
            <div className="continue-shopping-box">
              <a href={checkoutUrl} className="btn btn-light">
                Checkout
              </a>
            </div>
          </div>

          <div className="col-lg-5 col-md-5 col-sm-5 text-right">
            <Link to="/" className="btn btn-light">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>

      <div className="cart-totals">
        <h3>Cart Totals</h3>

        <ul>
          <li>
            Subtotal
            {/* <span>${total.toFixed(2)}</span> */}
            {cost?.subtotalAmount?.amount ? (
              <span>
                <Money data={cost?.subtotalAmount} />
              </span>
            ) : (
              '-'
            )}
          </li>
        </ul>

        <a href={checkoutUrl} className="btn btn-primary">
          Proceed to Checkout
        </a>
      </div>
    </>
  );
};

export default CartContent;
